const { getRoleGroup } = require('user-utils');

const factory = (Message, GatewayApi, Collection, Memoize, Uuid, $q, _) => {
  class MessageCollection extends Collection {
    static initClass() {
      this.prototype.$type = Message;
      this.prototype.$apiService = GatewayApi;
      this.prototype.$$cacheModels = true;

      this.prototype.clientId = null;
      this.prototype.viewerId = null;
      this.prototype.limit = 40;
      this.prototype.before = new Date().getTime();
      this.prototype.preview = false;
      this.prototype.full = true;
      this.prototype.stats = false;
      this.prototype.data = [];
      this.prototype.roles = [];
      this.prototype.inboxState = null;
      this.prototype.$lastResponseLength = null;

      this.prototype.$idSerializers = [
        'clientId',
        'viewerId',
        'full',
        'stats',
        'preview',
        'inboxState',
      ];

      this.prototype.$paramNames = {
        clientId: 'clientId',
        viewerId: 'viewerId',
        limit: 'limit',
        before: 'before',
        preview: 'preview',
        full: 'full',
        stats: 'stats',
        names() {
          return this.$$eventTypes();
        },

        group() {
          return getRoleGroup(this.roles);
        },
      };
    }

    $$eventTypes() {
      return ['feed.message.created', 'feed.message.edited'];
    }

    $$eventSourceIsClient(eventSource) {
      const allowedClientRoles = ['CLIENT', 'TAX_ACCOUNTANT'];
      const authorGroup = getRoleGroup(eventSource.authorRoles);

      if (allowedClientRoles.includes(authorGroup)) return true;

      if (eventSource.component === 'dropbox' && eventSource.sourceType === 'System') {
        return true;
      }

      return false;
    }

    $$originInEvent(e, origin) {
      const eventOrigin = _.find(e.assets, { payloadName: 'origin' });
      return origin.includes(eventOrigin != null ? eventOrigin.payload : undefined);
    }

    $url() {
      return `messages/${this.clientId}`;
    }

    $onMessage(e) {
      this.$add(this.$type.prototype.$serialize(e));
    }

    $onEdit(e) {
      const message = this.$add(this.$type.prototype.$serialize(e));
      this.$removeById(message.$getResourceId('message'));
    }

    $onCategorizationRequested(e) {
      this.$add(this.$type.prototype.$serialize(e));
    }

    $init() {
      super.$init();
      // Automatically fetch the first time this collection is created.
      if (this.$lastUpdated === 0 && this.clientId != null) {
        this.$fetch();
      }
    }

    $fetchMore() {
      if (!this.$resolved) {
        return $q.reject(new Error('MessageCollection not yet resolved'));
      }

      return this.$fetch({}, { before: this.getEarliestMessageDate() });
    }

    getEarliestMessageDate() {
      if (this.data.length === 0) return Date.now();

      return _.min(_.map(this.data, 'createdAt'));
    }

    $getLatest() {
      if (this.data.length === 0) return 0;

      return _.max(_.map(this.data, 'createdAt'));
    }

    $serializeMeta(data) {
      if (!data[0] || !data[0].events || !data[0].events.full) return {};

      const $lastResponseLength = data[0].events.full.length;
      return { $lastResponseLength };
    }

    $serialize(data) {
      if (!data[0] || !data[0].events || !data[0].events.full) return [];

      const serialized = _(data[0].events.full)
        .filter(message => this.$$eventTypes().includes(message.name))
        .filter(
          message =>
            // Filter out legacy events that don't have message bodies
            _.some(message.assets, { payloadName: 'message' }) ||
            _.some(message.assets, { payloadName: 'comment' }) ||
            message.name === 'event.categorization.requested',
        )
        .sortBy('createdAt')
        .value();

      return super.$serialize(serialized);
    }

    $post(text, assets, sms, sourceType) {
      let message = Message.$generate({
        viewerId: this.viewerId,
        clientId: this.clientId,
        roles: this.roles,
        inboxState: this.inboxState,
        assets,
        ...(sourceType && { sourceType }),
      }).addMessage(text);

      if (sms) {
        message = message.addPayload({ payloadName: 'sms', payload: sms });
      }

      message = message.$markAsViewed({ viewerId: this.viewerId, roles: this.roles }).$post();

      return this.$add(message);
    }

    $postCategorizationRequest() {
      const name = 'event.categorization.requested';

      const message = Message.$generate({
        viewerId: this.viewerId,
        clientId: this.clientId,
        roles: this.roles,
        name,
      })
        .$markAsViewed({ viewerId: this.viewerId, roles: this.roles })
        .$post();

      return this.$add(message);
    }

    isHistoryComplete() {
      return this.$lastResponseLength != null && this.$lastResponseLength < this.limit;
    }

    remoteMessages() {
      return this.data.filter(
        message => message.context.eventSource.authorId !== this.viewerId.toString(),
      );
    }

    localMessages() {
      return this.data.filter(
        message => message.context.eventSource.authorId === this.viewerId.toString(),
      );
    }

    unviewedMessages() {
      return this.remoteMessages().filter(message => !message.$isViewed(this.viewerId));
    }

    unviewedLoadedMessageCountBeforeTimestamp(timestamp) {
      const messages = this.unviewedMessages().filter(({ createdAt }) => createdAt <= timestamp);

      return messages.length;
    }

    unviewedCount() {
      return this.unviewedMessages().length;
    }

    $findFirstUnviewed() {
      return _.minBy(this.unviewedMessages(), 'createdAt') || {};
    }

    $markAllViewed() {
      if (!this.$resolved || this.unviewedCount() === 0) {
        return $q.when();
      }

      const before = this.$getLatest() + 1;
      const group = getRoleGroup(this.roles);

      return this.$apiCall({
        url: `messages/${this.clientId}`,
        method: 'PUT',
        params: {
          clientId: this.clientId,
          viewerId: this.viewerId,
          before,
          group,
        },
      }).then(() =>
        _.invokeMap(this.data, '$markAsViewed', {
          viewerId: this.viewerId,
          roles: this.roles,
        }),
      );
    }
  }

  MessageCollection.initClass();
  return MessageCollection;
};

const moduleDeps = [
  require('data/model/message').name,
  require('data/model/clientSettings').name,
  require('data/util').name,
];

module.exports = angular
  .module('bench.data.collection.message', moduleDeps)
  .factory('MessageCollection', factory);
